"use client";

import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  ADMIN_PAGES,
  type AdminPageId,
  type NavigationPage,
} from "@/app/navigation";
import { prefetchPage } from "@/app/pageRegistry";
import { getWorkflowPages, WORKFLOW_PAGE_FLOWS } from "@/app/workflow";
import { useThemeMode } from "@/theme/ThemeModeContext";

export type ShellUser = {
  id: string;
  name: string;
  initials: string;
  email: string;
  title: string;
  status: "Available" | "Busy" | "Away";
  countries: string[];
  languages: string[];
  assetLibrary: string;
  avatarUrl?: string;
};

export type WorkContext = {
  supplierProvider: string;
  campaign: string;
  dealz: string;
  asset: string;
  libraryOwner: string;
  country: string;
  language: string;
  entity: "Products" | "Services" | "Products + Services";
};

export type SearchRecord = {
  id: string;
  type:
    | "Page"
    | "Supplier/Provider"
    | "Campaign"
    | "Dealz"
    | "Asset"
    | "User"
    | "Command";
  title: string;
  subtitle: string;
  pageId: AdminPageId;
  icon: string;
  keywords: string[];
};

export type ShellNotification = {
  id: string;
  type: "Work" | "Localization" | "Automation" | "Security" | "LiveDealz";
  title: string;
  detail: string;
  time: string;
  unread: boolean;
  pageId: AdminPageId;
  tone: "orange" | "green" | "red" | "blue" | "violet";
};

export type PremiumAdminAppShellProps = {
  children?: React.ReactNode;
  activePage?: AdminPageId;
  onNavigate?: (page: NavigationPage) => void;
  permissions?: string[];
  user?: Partial<ShellUser>;
  environment?: "Production" | "Staging" | "Development";
  initialCollapsed?: boolean;
  initialContext?: Partial<WorkContext>;
  onContextChange?: (context: WorkContext) => void;
  searchRecords?: SearchRecord[];
  notifications?: ShellNotification[];
  badges?: { unreadInbox?: number; openAssignedWork?: number };
  onLogout?: () => void;
  contentClassName?: string;
};

const GUEST_USER: ShellUser = {
  id: "",
  name: "Guest",
  initials: "G",
  email: "",
  title: "",
  status: "Available",
  countries: [],
  languages: [],
  assetLibrary: "",
};

const DEFAULT_CONTEXT: WorkContext = {
  supplierProvider: "All Supplier/Providers",
  campaign: "All campaigns",
  dealz: "All Dealz",
  asset: "All assets",
  libraryOwner: "All user libraries",
  country: "Global",
  language: "All languages",
  entity: "Products + Services",
};

const COUNTRY_OPTIONS = ["Global", "Uganda", "Kenya", "Rwanda", "DR Congo"];
const LANGUAGE_OPTIONS = [
  "All languages",
  "English",
  "French",
  "Swahili",
  "Kinyarwanda",
  "Lingala",
  "Luganda",
];
const ENTITY_OPTIONS: WorkContext["entity"][] = [
  "Products + Services",
  "Products",
  "Services",
];

const QUICK_CREATE_ACTIONS = [
  {
    icon: "building",
    title: "Supplier/Provider",
    detail: "Identity, verification, portal and API",
    pageId: "suppliers-providers" as AdminPageId,
  },
  {
    icon: "flag",
    title: "Campaign",
    detail: "Markets, languages, participants and Dealz",
    pageId: "campaigns" as AdminPageId,
  },
  {
    icon: "live",
    title: "Dealz / LiveDealz",
    detail: "Shoppable Video or Live Session",
    pageId: "dealz-control" as AdminPageId,
  },
  {
    icon: "asset",
    title: "Asset intake",
    detail: "Listing, promo or live-commerce package",
    pageId: "listing-intake" as AdminPageId,
  },
  {
    icon: "users",
    title: "Invite user",
    detail: "Role, scopes, MFA and library ownership",
    pageId: "roles-permissions" as AdminPageId,
  },
  {
    icon: "spark",
    title: "API provider",
    detail: "Endpoint, vault reference, model and prompt",
    pageId: "automation" as AdminPageId,
  },
];

const BRAND_LOGO_SRC = "/brand/evzone-media-content-logo.png";
const BRAND_MARK_SRC = "/brand/evzone-media-content-mark.png";

const ICONS: Record<string, React.ReactNode> = {
  dashboard: (
    <>
      <rect x="3" y="3" width="7" height="7" rx="2" />
      <rect x="14" y="3" width="7" height="4" rx="2" />
      <rect x="14" y="11" width="7" height="10" rx="2" />
      <rect x="3" y="14" width="7" height="7" rx="2" />
    </>
  ),
  users: (
    <>
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M22 21v-2a4 4 0 0 0-3-3.9M16 3.2a4 4 0 0 1 0 7.6" />
    </>
  ),
  inbox: (
    <>
      <path d="M4 4h16v16H4z" />
      <path d="m4 13 4-4h8l4 4" />
      <path d="M8 13h2a2 2 0 0 0 4 0h2" />
    </>
  ),
  radio: (
    <>
      <path d="M5 19a10 10 0 0 1 0-14M9 15a5 5 0 0 1 0-6M19 5a10 10 0 0 1 0 14M15 9a5 5 0 0 1 0 6" />
      <circle cx="12" cy="12" r="1.5" />
    </>
  ),
  image: (
    <>
      <rect x="3" y="3" width="18" height="18" rx="3" />
      <circle cx="8.5" cy="8.5" r="1.5" />
      <path d="m21 15-5-5L5 21" />
    </>
  ),
  play: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="m10 8 6 4-6 4z" />
    </>
  ),
  type: (
    <>
      <path d="M4 7V4h16v3M9 20h6M12 4v16" />
    </>
  ),
  "media-check": (
    <>
      <rect x="3" y="4" width="18" height="16" rx="3" />
      <path d="m8 13 2.5 2.5L16 10M3 8h18" />
    </>
  ),
  "file-check": (
    <>
      <path d="M14 2H6a2 2 0 0 0-2 2v16h14a2 2 0 0 0 2-2V8z" />
      <path d="M14 2v6h6M8 15l2 2 4-4" />
    </>
  ),
  "shield-check": (
    <>
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10" />
      <path d="m9 12 2 2 4-4" />
    </>
  ),
  broadcast: (
    <>
      <path d="M8 16a6 6 0 0 1 0-8M16 8a6 6 0 0 1 0 8" />
      <path d="M5 19a10 10 0 0 1 0-14M19 5a10 10 0 0 1 0 14" />
      <circle cx="12" cy="12" r="2" />
    </>
  ),
  library: (
    <>
      <path d="m4 19.5 5-15 4 1.4-5 15z" />
      <path d="M14 4h6v16h-6z" />
    </>
  ),
  spark: (
    <>
      <path d="m12 3-1.6 4.4L6 9l4.4 1.6L12 15l1.6-4.4L18 9l-4.4-1.6z" />
      <path d="m5 14-.9 2.1L2 17l2.1.9L5 20l.9-2.1L8 17l-2.1-.9z" />
    </>
  ),
  chart: (
    <>
      <path d="M4 20V10M10 20V4M16 20v-7M22 20H2" />
    </>
  ),
  lock: (
    <>
      <rect x="4" y="10" width="16" height="11" rx="3" />
      <path d="M8 10V7a4 4 0 0 1 8 0v3" />
    </>
  ),
  panel: (
    <>
      <rect x="3" y="3" width="18" height="18" rx="3" />
      <path d="M9 3v18" />
    </>
  ),
  building: (
    <>
      <path d="M4 21V3h12v18M16 8h4v13M8 7h4M8 11h4M8 15h4M7 21h6" />
    </>
  ),
  flag: (
    <>
      <path d="M5 21V4M5 4h11l-1 4 1 4H5" />
    </>
  ),
  live: (
    <>
      <path d="M8.5 15.5a5 5 0 0 1 0-7M15.5 8.5a5 5 0 0 1 0 7" />
      <circle cx="12" cy="12" r="1.5" />
    </>
  ),
  bell: (
    <>
      <path d="M18 8a6 6 0 0 0-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9" />
      <path d="M10 21h4" />
    </>
  ),
  settings: (
    <>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.7 1.7 0 0 0 .3 1.9l-2.8 2.8a1.7 1.7 0 0 0-1.9-.3A1.7 1.7 0 0 0 14 21h-4a1.7 1.7 0 0 0-1-1.6 1.7 1.7 0 0 0-1.9.3L4.3 17a1.7 1.7 0 0 0 .3-1.9A1.7 1.7 0 0 0 3 14v-4a1.7 1.7 0 0 0 1.6-1 1.7 1.7 0 0 0-.3-1.9L7.1 4.3A1.7 1.7 0 0 0 9 4.6 1.7 1.7 0 0 0 10 3h4a1.7 1.7 0 0 0 1 1.6 1.7 1.7 0 0 0 1.9-.3L19.7 7a1.7 1.7 0 0 0-.3 1.9A1.7 1.7 0 0 0 21 10v4a1.7 1.7 0 0 0-1.6 1z" />
    </>
  ),
  search: (
    <>
      <circle cx="11" cy="11" r="7" />
      <path d="m20 20-4-4" />
    </>
  ),
  plus: (
    <>
      <path d="M12 5v14M5 12h14" />
    </>
  ),
  menu: (
    <>
      <path d="M4 7h16M4 12h16M4 17h16" />
    </>
  ),
  chevron: <path d="m9 18 6-6-6-6" />,
  down: <path d="m6 9 6 6 6-6" />,
  close: (
    <>
      <path d="m6 6 12 12M18 6 6 18" />
    </>
  ),
  command: (
    <>
      <path d="M18 9a3 3 0 1 0-3-3v12a3 3 0 1 0 3-3H6a3 3 0 1 0 3 3V6a3 3 0 1 0-3 3z" />
    </>
  ),
  globe: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M3 12h18M12 3a15 15 0 0 1 0 18M12 3a15 15 0 0 0 0 18" />
    </>
  ),
  user: (
    <>
      <circle cx="12" cy="8" r="4" />
      <path d="M4 21a8 8 0 0 1 16 0" />
    </>
  ),
  asset: (
    <>
      <rect x="3" y="3" width="18" height="18" rx="3" />
      <path d="m7 15 3-3 2 2 3-4 2 2" />
    </>
  ),
  check: <path d="m5 12 4 4L19 6" />,
  warning: (
    <>
      <path d="M12 3 2.5 20h19z" />
      <path d="M12 9v4M12 17h.01" />
    </>
  ),
  arrow: (
    <>
      <path d="M5 12h14M14 7l5 5-5 5" />
    </>
  ),
  filter: (
    <>
      <path d="M4 5h16M7 12h10M10 19h4" />
    </>
  ),
  help: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M9.7 9a2.5 2.5 0 1 1 4.5 1.5c-.9 1-2.2 1.2-2.2 2.8M12 17h.01" />
    </>
  ),
  shield: <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10" />,
  logout: (
    <>
      <path d="M10 17l5-5-5-5M15 12H3M21 19V5a2 2 0 0 0-2-2h-6" />
    </>
  ),
  moon: (
    <>
      <path d="M21 12.8A9 9 0 1 1 11.2 3 7 7 0 0 0 21 12.8z" />
    </>
  ),
  monitor: (
    <>
      <rect x="3" y="4" width="18" height="13" rx="2" />
      <path d="M8 21h8M12 17v4" />
    </>
  ),
  sun: (
    <>
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
    </>
  ),
};

function Icon({ name, size = 18 }: { name: string; size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {ICONS[name] ?? ICONS.dashboard}
    </svg>
  );
}
function cx(...v: Array<string | false | null | undefined>) {
  return v.filter(Boolean).join(" ");
}
function hasPermission(grants: string[], required: string) {
  return (
    grants.includes("*") ||
    grants.includes(required) ||
    grants.some((g) => g.endsWith(".*") && required.startsWith(g.slice(0, -1)))
  );
}
function Badge({
  children,
  tone = "neutral",
}: {
  children: React.ReactNode;
  tone?: NavigationPage["badgeTone"];
}) {
  return <span className={`ev-badge ${tone}`}>{children}</span>;
}



export default function PremiumAdminAppShell({
  children,
  activePage = "command-center",
  onNavigate,
  permissions = ["*"],
  user: partialUser,
  environment = "Production",
  initialCollapsed = false,
  initialContext,
  onContextChange,
  searchRecords = [],
  notifications: propNotifications,
  badges,
  onLogout,
  contentClassName,
}: PremiumAdminAppShellProps) {
  const { themeMode, toggleThemeMode } = useThemeMode();

  const user = useMemo<ShellUser>(() => {
    return partialUser
      ? ({ ...GUEST_USER, ...partialUser } as ShellUser)
      : GUEST_USER;
  }, [partialUser]);

  // Context selectors no longer fetch dynamic catalog data inside the shell.
  // This keeps the app shell lightweight and avoids duplicate data
  // subscriptions on every navigation.
  const supplierOptions = [DEFAULT_CONTEXT.supplierProvider];
  const campaignOptions = [DEFAULT_CONTEXT.campaign];
  const dealOptions = [DEFAULT_CONTEXT.dealz];
  const assetOptions = [DEFAULT_CONTEXT.asset];
  const libraryOwnerOptions = [DEFAULT_CONTEXT.libraryOwner];

  const systemStatus = useMemo(
    () => ({
      label: "Systems operational",
      detail: "Context providers not loaded in shell",
      readiness: 100,
    }),
    [],
  );

  const [collapsed, setCollapsed] = useState(initialCollapsed);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openMenu, setOpenMenu] = useState<
    | "workspace"
    | "quick"
    | "notifications"
    | "profile"
    | "context"
    | "help"
    | null
  >(null);
  const [commandOpen, setCommandOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [context, setContext] = useState<WorkContext>({
    ...DEFAULT_CONTEXT,
    ...initialContext,
  });

  const [notifications, setNotifications] = useState(propNotifications ?? []);
  useEffect(() => {
    setNotifications(propNotifications ?? []);
  }, [propNotifications]);

  const searchInput = useRef<HTMLInputElement>(null);
  const visiblePages = useMemo(
    () =>
      ADMIN_PAGES.filter(
        (p) => p.id !== "app-shell" && !p.hidden && hasPermission(permissions, p.permission),
      ),
    [permissions],
  );
  const active = ADMIN_PAGES.find((p) => p.id === activePage) ?? ADMIN_PAGES[0];
  const flow = WORKFLOW_PAGE_FLOWS[active.id];
  const previousFlowPages = useMemo(
    () =>
      getWorkflowPages(flow.receivesFrom).filter((p) =>
        hasPermission(permissions, p.permission),
      ),
    [flow, permissions],
  );
  const nextFlowPages = useMemo(
    () =>
      getWorkflowPages(flow.sendsTo).filter((p) =>
        hasPermission(permissions, p.permission),
      ),
    [flow, permissions],
  );
  const previousFlowPage = previousFlowPages[0];
  const nextFlowPage = nextFlowPages[0];

  const records = useMemo<SearchRecord[]>(
    () => [
      ...visiblePages.map((p) => ({
        id: `PAGE-${p.number}`,
        type: "Page" as const,
        title: p.label,
        subtitle: `Page ${p.number} · ${p.description}`,
        pageId: p.id,
        icon: p.icon,
        keywords: [...p.keywords, p.shortLabel],
      })),
      ...searchRecords,
    ],
    [visiblePages, searchRecords],
  );
  const filtered = useMemo(() => {
    const q = query.toLowerCase().trim();
    return (
      q
        ? records.filter((r) =>
            [r.title, r.subtitle, ...r.keywords]
              .join(" ")
              .toLowerCase()
              .includes(q),
          )
        : records
    ).slice(0, 12);
  }, [records, query]);
  const unread = badges?.unreadInbox ?? notifications.filter((n) => n.unread).length;

  const appShellMetrics = useMemo(
    () => [
      {
        label: "Permission-aware routes",
        value: String(visiblePages.length),
        note: "All planned pages mapped",
        tone: "violet",
        icon: "lock",
      },
      {
        label: "Searchable objects",
        value: String(records.length),
        note: "Pages and commands",
        tone: "blue",
        icon: "search",
      },
      {
        label: "Open notifications",
        value: String(unread),
        note: "Work, access and systems",
        tone: "orange",
        icon: "bell",
      },
      {
        label: "Provider readiness",
        value: `${systemStatus.readiness}%`,
        note: systemStatus.detail,
        tone: systemStatus.readiness >= 90 ? "green" : "warning",
        icon: "spark",
      },
    ],
    [visiblePages.length, records.length, unread, systemStatus],
  );

  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setCommandOpen(true);
        setTimeout(() => searchInput.current?.focus(), 30);
      }
      if (e.key === "Escape") {
        setOpenMenu(null);
        setCommandOpen(false);
        setMobileOpen(false);
      }
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, []);

  useEffect(() => {
    if (!openMenu) return;
    const h = (e: PointerEvent) => {
      const target = e.target;
      if (!(target instanceof Element)) return;
      if (
        target.closest(
          ".top-popover,.sidebar-popover,.workspace-switch,.context-trigger,.quick-button,.alert-button,.profile-trigger,.help-trigger",
        )
      )
        return;
      setOpenMenu(null);
    };
    document.addEventListener("pointerdown", h, true);
    return () => document.removeEventListener("pointerdown", h, true);
  }, [openMenu]);

  const navigate = (page: NavigationPage) => {
    onNavigate?.(page);
    setMobileOpen(false);
    setCommandOpen(false);
    setOpenMenu(null);
  };
  const selectRecord = (record: SearchRecord) => {
    const page = ADMIN_PAGES.find((p) => p.id === record.pageId);
    if (page) navigate(page);
  };
  const updateContext = (patch: Partial<WorkContext>) => {
    const next = { ...context, ...patch };
    setContext(next);
    onContextChange?.(next);
  };
  const markAllRead = () =>
    setNotifications((n) => n.map((x) => ({ ...x, unread: false })));

  return (
    <div
      className={cx(
        "ev-shell",
        collapsed && "is-collapsed",
        mobileOpen && "mobile-open",
      )}
      data-theme={themeMode}
    >
      <style>{SHELL_CSS}</style>
      <aside className="ev-sidebar" aria-label="Primary navigation">
        <div className="brand-row">
          <button
            className="brand"
            onClick={() => navigate(ADMIN_PAGES[0])}
            aria-label="EVzone Media & Content home"
          >
            <img
              className="brand-logo"
              src={BRAND_LOGO_SRC}
              alt="EVzone Media & Content"
            />
            <img
              className="brand-icon-image"
              src={BRAND_MARK_SRC}
              alt=""
              aria-hidden="true"
            />
          </button>
          <button
            className="icon-button collapse-button"
            onClick={() => setCollapsed((x) => !x)}
            aria-label={collapsed ? "Expand navigation" : "Collapse navigation"}
          >
            <Icon name="panel" />
          </button>
          <button
            className="icon-button mobile-close"
            onClick={() => setMobileOpen(false)}
          >
            <Icon name="close" />
          </button>
        </div>
        <button
          className="workspace-switch"
          onClick={() =>
            setOpenMenu(openMenu === "workspace" ? null : "workspace")
          }
        >
          <span className="workspace-logo">MC</span>
          <span className="workspace-copy">
            <strong>Media & Content</strong>
          </span>
          <Icon name="down" size={14} />
        </button>
        {openMenu === "workspace" && (
          <div className="sidebar-popover workspace-menu">
            <button className="active">
              <span>MC</span>
              <div>
                <strong>Media & Content</strong>
                <small>Production workspace</small>
              </div>
              <Icon name="check" />
            </button>
            <button
              onClick={() => {
                const p = ADMIN_PAGES.find((x) => x.id === "dealz-control");
                if (p) navigate(p);
              }}
            >
              <span>LC</span>
              <div>
                <strong>Live Commerce</strong>
                <small>Dealz operations</small>
              </div>
            </button>
            <button
              onClick={() => {
                const p = ADMIN_PAGES.find((x) => x.id === "platform-settings");
                if (p) navigate(p);
              }}
            >
              <span>PA</span>
              <div>
                <strong>Platform Admin</strong>
                <small>Policies and access</small>
              </div>
            </button>
          </div>
        )}
        <nav className="nav-scroll">
          <div className="group-pages">
            {visiblePages.map((page) => (
              <button
                key={page.id}
                className={cx("nav-item", activePage === page.id && "active")}
                onClick={() => navigate(page)}
                onMouseEnter={() => prefetchPage(page.id)}
                title={collapsed ? page.label : undefined}
              >
                <span className="nav-icon">
                  <Icon name={page.icon} />
                </span>
                <span className="nav-copy">
                  <strong>{page.shortLabel}</strong>
                </span>
                {page.badge && (
                  <Badge tone={page.badgeTone}>{page.badge}</Badge>
                )}
              </button>
            ))}
          </div>
        </nav>
        <div className="sidebar-footer">
          <button
            className="nav-item help-trigger"
            onClick={() => setOpenMenu(openMenu === "help" ? null : "help")}
          >
            <span className="nav-icon">
              <Icon name="help" />
            </span>
            <span className="nav-copy">
              <strong>Help</strong>
            </span>
          </button>
          {openMenu === "help" && (
            <div className="sidebar-popover help-menu">
              <header>
                <div>
                  <small>HELP & PLAYBOOKS</small>
                  <h3>Guides, shortcuts and support</h3>
                </div>
                <button onClick={() => setOpenMenu(null)}>
                  <Icon name="close" size={15} />
                </button>
              </header>
              <button
                onClick={() => {
                  const p = ADMIN_PAGES.find((x) => x.id === "team-operations");
                  if (p) navigate(p);
                }}
              >
                <span>
                  <Icon name="users" />
                </span>
                <div>
                  <strong>Assignment playbook</strong>
                  <small>Routing, skills, languages and coverage.</small>
                </div>
              </button>
              <button
                onClick={() => {
                  const p = ADMIN_PAGES.find((x) => x.id === "inbox");
                  if (p) navigate(p);
                }}
              >
                <span>
                  <Icon name="bell" />
                </span>
                <div>
                  <strong>Support requests</strong>
                  <small>Tasks, mentions and access requests.</small>
                </div>
              </button>
            </div>
          )}
          <div className="system-mini">
            <span className="health-dot" />
            <div>
              <strong>{systemStatus.label}</strong>
            </div>
          </div>
        </div>
      </aside>

      <div className="ev-main">
        <header className="ev-topbar">
          <div className="topbar-left">
            <button
              className="icon-button mobile-menu"
              onClick={() => setMobileOpen(true)}
            >
              <Icon name="menu" />
            </button>
          </div>
          <button
            className="universal-search"
            onClick={() => {
              setCommandOpen(true);
              setTimeout(() => searchInput.current?.focus(), 30);
            }}
          >
            <Icon name="search" />
            <span>
              Search pages and commands…
            </span>
            <kbd>⌘ K</kbd>
          </button>
          <div className="topbar-actions">
            <button
              className="context-trigger"
              onClick={() =>
                setOpenMenu(openMenu === "context" ? null : "context")
              }
            >
              <span className="context-orb">SP</span>
              <span>
                <small>Operating context</small>
                <strong>{context.campaign}</strong>
              </span>
              <Icon name="down" size={13} />
            </button>
            <button
              className="quick-button"
              onClick={() => setOpenMenu(openMenu === "quick" ? null : "quick")}
            >
              <Icon name="plus" />
              <span>Create</span>
            </button>
            <button
              className="icon-button theme-toggle"
              onClick={toggleThemeMode}
              aria-label={
                themeMode === "light"
                  ? "Switch to dark theme"
                  : "Switch to light theme"
              }
              title={
                themeMode === "light"
                  ? "Switch to dark theme"
                  : "Switch to light theme"
              }
            >
              <Icon name={themeMode === "light" ? "moon" : "sun"} />
            </button>
            <button
              className="icon-button alert-button"
              onClick={() =>
                setOpenMenu(
                  openMenu === "notifications" ? null : "notifications",
                )
              }
            >
              <Icon name="bell" />
              {unread > 0 && <b>{unread}</b>}
            </button>
            <button
              className="profile-trigger"
              onClick={() =>
                setOpenMenu(openMenu === "profile" ? null : "profile")
              }
            >
              <span className="avatar">{user.initials}</span>
              <span>
                <strong>{user.name}</strong>
                <small>{user.title}</small>
              </span>
              <Icon name="down" size={13} />
            </button>
          </div>
          {openMenu === "context" && (
            <div className="top-popover context-menu">
              <header>
                <div>
                  <small>OPERATING CONTEXT</small>
                  <h3>Scope every page consistently</h3>
                </div>
                <button onClick={() => setOpenMenu(null)}>
                  <Icon name="close" />
                </button>
              </header>
              <div className="lineage-flow">
                <span>Supplier/Provider</span>
                <Icon name="chevron" size={13} />
                <span>Campaign</span>
                <Icon name="chevron" size={13} />
                <span>Dealz</span>
                <Icon name="chevron" size={13} />
                <span>Asset</span>
              </div>
              <div className="context-form">
                <label>
                  <span>Supplier/Provider</span>
                  <select
                    value={context.supplierProvider}
                    onChange={(e) =>
                      updateContext({ supplierProvider: e.target.value })
                    }
                  >
                    {supplierOptions.map((value) => (
                      <option key={value}>{value}</option>
                    ))}
                  </select>
                </label>
                <label>
                  <span>Campaign</span>
                  <select
                    value={context.campaign}
                    onChange={(e) =>
                      updateContext({ campaign: e.target.value })
                    }
                  >
                    {campaignOptions.map((value) => (
                      <option key={value}>{value}</option>
                    ))}
                  </select>
                </label>
                <label>
                  <span>Dealz</span>
                  <select
                    value={context.dealz}
                    onChange={(e) => updateContext({ dealz: e.target.value })}
                  >
                    {dealOptions.map((value) => (
                      <option key={value}>{value}</option>
                    ))}
                  </select>
                </label>
                <label>
                  <span>Asset</span>
                  <select
                    value={context.asset}
                    onChange={(e) => updateContext({ asset: e.target.value })}
                  >
                    {assetOptions.map((value) => (
                      <option key={value}>{value}</option>
                    ))}
                  </select>
                </label>
                <label>
                  <span>User library</span>
                  <select
                    value={context.libraryOwner}
                    onChange={(e) =>
                      updateContext({ libraryOwner: e.target.value })
                    }
                  >
                    {libraryOwnerOptions.map((value) => (
                      <option key={value}>{value}</option>
                    ))}
                  </select>
                </label>
                <label>
                  <span>Country</span>
                  <select
                    value={context.country}
                    onChange={(e) => updateContext({ country: e.target.value })}
                  >
                    {COUNTRY_OPTIONS.map((value) => (
                      <option key={value}>{value}</option>
                    ))}
                  </select>
                </label>
                <label>
                  <span>Language</span>
                  <select
                    value={context.language}
                    onChange={(e) =>
                      updateContext({ language: e.target.value })
                    }
                  >
                    {LANGUAGE_OPTIONS.map((value) => (
                      <option key={value}>{value}</option>
                    ))}
                  </select>
                </label>
                <label>
                  <span>Entity</span>
                  <select
                    value={context.entity}
                    onChange={(e) =>
                      updateContext({
                        entity: e.target.value as WorkContext["entity"],
                      })
                    }
                  >
                    {ENTITY_OPTIONS.map((value) => (
                      <option key={value}>{value}</option>
                    ))}
                  </select>
                </label>
              </div>
              <footer>
                <button
                  className="button ghost"
                  onClick={() => {
                    setContext(DEFAULT_CONTEXT);
                    onContextChange?.(DEFAULT_CONTEXT);
                  }}
                >
                  Reset scope
                </button>
                <button
                  className="button primary"
                  onClick={() => setOpenMenu(null)}
                >
                  Apply context
                </button>
              </footer>
            </div>
          )}
          {openMenu === "quick" && (
            <div className="top-popover quick-menu">
              <header>
                <div>
                  <small>QUICK CREATE</small>
                  <h3>Start a governed workflow</h3>
                </div>
              </header>
              {QUICK_CREATE_ACTIONS.map((a) => (
                <button
                  key={a.title}
                  onClick={() => {
                    const p = ADMIN_PAGES.find((x) => x.id === a.pageId);
                    if (p) navigate(p);
                  }}
                >
                  <span>
                    <Icon name={a.icon} />
                  </span>
                  <div>
                    <strong>{a.title}</strong>
                    <small>{a.detail}</small>
                  </div>
                  <Icon name="chevron" size={14} />
                </button>
              ))}
            </div>
          )}
          {openMenu === "notifications" && (
            <div className="top-popover notification-menu">
              <header>
                <div>
                  <small>NOTIFICATIONS</small>
                  <h3>{unread} unread updates</h3>
                </div>
                <button onClick={markAllRead}>Mark all read</button>
              </header>
              <div className="notification-list">
                {notifications.map((n) => (
                  <button
                    key={n.id}
                    className={cx(n.unread && "unread")}
                    onClick={() => {
                      setNotifications((x) =>
                        x.map((v) =>
                          v.id === n.id ? { ...v, unread: false } : v,
                        ),
                      );
                      const p = ADMIN_PAGES.find((x) => x.id === n.pageId);
                      if (p) navigate(p);
                    }}
                  >
                    <span className={`notice-icon ${n.tone}`}>
                      <Icon
                        name={
                          n.type === "Security"
                            ? "shield"
                            : n.type === "Localization"
                              ? "globe"
                              : n.type === "LiveDealz"
                                ? "live"
                                : n.type === "Automation"
                                  ? "spark"
                                  : "bell"
                        }
                      />
                    </span>
                    <div>
                      <strong>{n.title}</strong>
                      <p>{n.detail}</p>
                      <small>
                        {n.type} · {n.time}
                      </small>
                    </div>
                    {n.unread && <i />}
                  </button>
                ))}
              </div>
              <footer>
                <button
                  onClick={() => {
                    const p = ADMIN_PAGES.find((x) => x.id === "inbox");
                    if (p) navigate(p);
                  }}
                >
                  Open full inbox <Icon name="arrow" />
                </button>
              </footer>
            </div>
          )}
          {openMenu === "profile" && (
            <div className="top-popover profile-menu">
              <div className="profile-hero">
                <span className="avatar large">{user.initials}</span>
                <div>
                  <strong>{user.name}</strong>
                  <small>{user.email}</small>
                  <p>
                    <i /> {user.status} · {environment}
                  </p>
                </div>
              </div>
              <div className="profile-scope">
                <span>
                  <Icon name="globe" /> {user.countries.join(", ")}
                </span>
                <span>
                  <Icon name="type" /> {user.languages.join(" · ")}
                </span>
                <span>
                  <Icon name="library" /> {user.assetLibrary}
                </span>
              </div>
              <button
                onClick={() => {
                  const p = ADMIN_PAGES.find(
                    (x) => x.id === "platform-settings",
                  );
                  if (p) navigate(p);
                }}
              >
                <Icon name="user" />
                <span>
                  <strong>Profile & preferences</strong>
                  <small>Identity, locale and notification settings</small>
                </span>
              </button>
              <button
                onClick={() => {
                  const p = ADMIN_PAGES.find(
                    (x) => x.id === "roles-permissions",
                  );
                  if (p) navigate(p);
                }}
              >
                <Icon name="lock" />
                <span>
                  <strong>My access</strong>
                  <small>Roles, scopes and temporary grants</small>
                </span>
              </button>
              <button
                onClick={() => {
                  const p = ADMIN_PAGES.find((x) => x.id === "analytics-audit");
                  if (p) navigate(p);
                }}
              >
                <Icon name="monitor" />
                <span>
                  <strong>Active sessions</strong>
                  <small>
                    {user.name} · {user.email}
                  </small>
                </span>
              </button>
              <button
                className="danger"
                onClick={() => {
                  setOpenMenu(null);
                  setCommandOpen(false);
                  setMobileOpen(false);
                  onLogout?.();
                }}
              >
                <Icon name="logout" />
                <span>
                  <strong>Sign out</strong>
                  <small>End this secure session</small>
                </span>
              </button>
            </div>
          )}
        </header>

        <div className="context-strip">
          <div className="context-lineage">
            <span>
              <b>SP</b>
              {context.supplierProvider}
            </span>
            <Icon name="chevron" size={12} />
            <span>
              <b>C</b>
              {context.campaign}
            </span>
            <Icon name="chevron" size={12} />
            <span>
              <b>D</b>
              {context.dealz}
            </span>
            <Icon name="chevron" size={12} />
            <span>
              <b>A</b>
              {context.asset}
            </span>
          </div>
          <div className="context-flow" aria-label="Workflow handoff shortcuts">
            {previousFlowPage && (
              <button
                type="button"
                onClick={() => navigate(previousFlowPage)}
                title={`Open upstream workspace: ${previousFlowPage.label}`}
              >
                <small>From</small>
                <strong>{previousFlowPage.shortLabel}</strong>
              </button>
            )}
            {nextFlowPage && (
              <button
                type="button"
                className="primary"
                onClick={() => navigate(nextFlowPage)}
                title={`Open next workspace: ${nextFlowPage.label}`}
              >
                <small>Next</small>
                <strong>{nextFlowPage.shortLabel}</strong>
                <Icon name="chevron" size={12} />
              </button>
            )}
          </div>
          <div className="context-meta">
            <span>
              <Icon name="library" />
              {context.libraryOwner}
            </span>
            <span>
              <Icon name="globe" />
              {context.country} · {context.language}
            </span>
            <Badge tone={environment === "Production" ? "success" : "warning"}>
              {environment}
            </Badge>
          </div>
        </div>

        <main className={cx("ev-content", contentClassName)}>
          {children ?? (
            <AppShellOverview
              active={active}
              navigate={navigate}
              metrics={appShellMetrics}
            />
          )}
        </main>
      </div>
      {mobileOpen && (
        <button
          className="mobile-scrim"
          onClick={() => setMobileOpen(false)}
          aria-label="Close navigation"
        />
      )}

      {commandOpen && (
        <div
          className="command-backdrop"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setCommandOpen(false);
          }}
        >
          <section className="command-palette">
            <header>
              <Icon name="search" />
              <input
                ref={searchInput}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search the entire EVzone Admin…"
                autoFocus
              />
              <kbd>Esc</kbd>
            </header>
            <div className="command-filters">
              <button className="active">Everything</button>
              <button>Pages</button>
              <button>Supplier/Providers</button>
              <button>Campaigns</button>
              <button>Dealz</button>
              <button>Assets</button>
              <span>{filtered.length} results</span>
            </div>
            <div className="command-results">
              {filtered.map((r, i) => (
                <button key={r.id} onClick={() => selectRecord(r)}>
                  <span className="result-icon">
                    <Icon name={r.icon} />
                  </span>
                  <div>
                    <strong>{r.title}</strong>
                    <small>
                      {r.type} · {r.subtitle}
                    </small>
                  </div>
                  {i < 9 && <kbd>⌘{i + 1}</kbd>}
                  <Icon name="chevron" size={14} />
                </button>
              ))}
            </div>
            <footer>
              <span>
                <kbd>↑↓</kbd> Navigate
              </span>
              <span>
                <kbd>↵</kbd> Open
              </span>
              <span>
                <kbd>⌘K</kbd> Toggle
              </span>
              <b>Permission-filtered results</b>
            </footer>
          </section>
        </div>
      )}
    </div>
  );
}

function AppShellOverview({
  active,
  navigate,
  metrics,
}: {
  active: NavigationPage;
  navigate: (p: NavigationPage) => void;
  metrics: {
    label: string;
    value: string;
    note: string;
    tone: string;
    icon: string;
  }[];
}) {
  return (
    <div className="shell-preview-page">
      <section className="preview-hero">
        <div>
          <span className="eyebrow">SHARED APPLICATION FRAME · PAGE 16</span>
          <h1>
            One premium operating system for every media, content and LiveDealz
            workflow.
          </h1>
          <p>
            The AppShell composes permission-aware navigation, universal search,
            Supplier/Provider → Campaign → Dealz → Asset context, per-user
            libraries, notifications and secure session controls around all
            route workspaces.
          </p>
          <div className="hero-actions">
            <button
              className="button primary"
              onClick={() => navigate(ADMIN_PAGES[0])}
            >
              Open Command Center <Icon name="arrow" />
            </button>
            <button
              className="button ghost"
              onClick={() => navigate(ADMIN_PAGES[16])}
            >
              Manage Supplier/Providers
            </button>
          </div>
        </div>
        <aside>
          <div className="orbit">
            <span className="orbit-core">EV</span>
            <i className="o1">SP</i>
            <i className="o2">C</i>
            <i className="o3">D</i>
            <i className="o4">A</i>
          </div>
          <small>ACTIVE ROUTE</small>
          <strong>{active.label}</strong>
          <p>{active.route}</p>
        </aside>
      </section>
      <section className="preview-metrics">
        {metrics.map((m) => (
          <article key={m.label}>
            <span className={`metric-icon ${m.tone}`}>
              <Icon name={m.icon} />
            </span>
            <div>
              <small>{m.label}</small>
              <strong>{m.value}</strong>
              <p>{m.note}</p>
            </div>
          </article>
        ))}
      </section>
      <section className="preview-grid">
        <article className="preview-panel route-map">
          <header>
            <div>
              <span className="eyebrow">ROUTE COVERAGE</span>
              <h2>All page families, one consistent shell</h2>
            </div>
            <Badge tone="success">
              {ADMIN_PAGES.length} / {ADMIN_PAGES.length} mapped
            </Badge>
          </header>
          <div className="route-family-grid">
            <button onClick={() => navigate(ADMIN_PAGES[0])}>
              <span>
                <Icon name="dashboard" />
              </span>
              <div>
                <strong>Navigation</strong>
                <small>
                  {ADMIN_PAGES.filter((p) => p.id !== "app-shell").length}{" "}
                  routes
                </small>
              </div>
              <Icon name="chevron" />
            </button>
          </div>
        </article>
        <article className="preview-panel access-card">
          <header>
            <div>
              <span className="eyebrow">ACCESS POSTURE</span>
              <h2>Scoped by identity and context</h2>
            </div>
            <Icon name="shield" />
          </header>
          <div className="access-ring">
            <div>
              <strong>100%</strong>
              <small>MFA coverage</small>
            </div>
          </div>
          <ul>
            <li>
              <span />
              <div>
                <strong>Permission-filtered navigation</strong>
                <small>
                  Hidden routes never appear in the shell or search.
                </small>
              </div>
            </li>
            <li>
              <span />
              <div>
                <strong>Context inheritance</strong>
                <small>
                  Country, language and library scopes follow every page.
                </small>
              </div>
            </li>
            <li>
              <span />
              <div>
                <strong>No frontend secrets</strong>
                <small>
                  Only vault references and connection states are displayed.
                </small>
              </div>
            </li>
          </ul>
        </article>
        <article className="preview-panel workflow-card">
          <header>
            <div>
              <span className="eyebrow">LIVEDEALZ LINEAGE</span>
              <h2>Navigate without losing operating context</h2>
            </div>
          </header>
          <div className="workflow-line">
            <span>
              <b>SP</b>
              <small>Supplier/Provider</small>
              <strong>—</strong>
            </span>
            <Icon name="chevron" />
            <span>
              <b>C</b>
              <small>Campaign</small>
              <strong>—</strong>
            </span>
            <Icon name="chevron" />
            <span>
              <b>D</b>
              <small>Dealz</small>
              <strong>—</strong>
            </span>
            <Icon name="chevron" />
            <span>
              <b>A</b>
              <small>Asset</small>
              <strong>—</strong>
            </span>
          </div>
          <footer>
            <span>
              <Icon name="library" /> Select a user library
            </span>
            <span>
              <Icon name="globe" /> Country · Language · Entity
            </span>
          </footer>
        </article>
      </section>
    </div>
  );
}
const SHELL_CSS = `
:root{--ink:#17201e;--muted:#6e7b78;--line:#e3e8e6;--soft:#f5f7f6;--orange:#ff7a00;--orange2:#ff9f3d;--green:#147a5f;--greenSoft:#e7f7f1;--red:#cc4545;--redSoft:#fff0ef;--blue:#286fce;--blueSoft:#edf5ff;--violet:#7658d8;--violetSoft:#f1edff;--shadow:0 20px 50px rgba(28,40,36,.10);--sidebar:286px;--topbar:76px;--ev-font-family:Inter,ui-sans-serif,system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,Arial,sans-serif;--ev-font-size-base:16px;--ev-line-height-base:1.45;--ev-font-size-display:34px;--ev-font-size-title:24px;--ev-font-size-section:20px;--ev-font-size-body:16px;--ev-font-size-control:15px;--ev-font-size-meta:14px;--ev-font-size-caption:12px;font-family:var(--ev-font-family);font-size:var(--ev-font-size-base);line-height:var(--ev-line-height-base);color:var(--ink)}
*{box-sizing:border-box}.ev-shell{min-height:100vh;background:#f3f5f4;color:var(--ink)}button,input,select,textarea{font:inherit}.ev-sidebar{position:fixed;inset:0 auto 0 0;width:var(--sidebar);background:linear-gradient(180deg,#111a18,#17221f 58%,#101816);color:#fff;z-index:50;display:flex;flex-direction:column;border-right:1px solid rgba(255,255,255,.06);transition:width .25s cubic-bezier(.2,.8,.2,1);box-shadow:12px 0 40px rgba(5,12,10,.12)}.ev-main{margin-left:var(--sidebar);transition:margin-left .25s cubic-bezier(.2,.8,.2,1)}.brand-row{height:82px;display:flex;align-items:center;padding:0 12px 0 16px;border-bottom:1px solid rgba(255,255,255,.08);gap:8px}.brand{height:58px;display:flex;align-items:center;background:transparent;border:0;border-radius:12px;cursor:pointer;min-width:0;flex:1;text-align:left;overflow:hidden;padding:5px 8px;box-shadow:none}.brand-logo{display:block;width:190px;max-width:100%;height:46px;object-fit:contain;object-position:left center}.brand-icon-image{display:none;width:42px;height:42px;object-fit:contain;border-radius:11px;background:transparent}.icon-button{border:1px solid transparent;background:transparent;color:inherit;width:38px;height:38px;border-radius:12px;display:grid;place-items:center;cursor:pointer}.icon-button:hover{background:rgba(255,255,255,.08)}.mobile-close,.mobile-menu{display:none}.workspace-switch{margin:12px 12px 7px;padding:10px;border:1px solid rgba(255,255,255,.08);border-radius:13px;background:rgba(255,255,255,.04);color:#fff;display:flex;align-items:center;gap:9px;cursor:pointer;text-align:left}.workspace-logo{width:29px;height:29px;border-radius:9px;background:rgba(255,122,0,.16);color:#ffae60;display:grid;place-items:center;font-size:12px;font-weight:900}.workspace-copy{min-width:0;flex:1}.workspace-copy small,.workspace-copy strong{display:block}.workspace-copy small{font-size:12px;color:#83938e}.workspace-copy strong{font-size:12px;margin-top:2px}.sidebar-popover{position:absolute;left:12px;right:12px;top:135px;background:#fff;color:var(--ink);border-radius:14px;padding:7px;box-shadow:0 20px 60px rgba(0,0,0,.32);z-index:70}.workspace-menu button{width:100%;border:0;background:#fff;display:flex;align-items:center;gap:8px;padding:9px;border-radius:9px;text-align:left;cursor:pointer}.workspace-menu button:hover,.workspace-menu button.active{background:var(--soft)}.workspace-menu button>span{width:30px;height:30px;border-radius:8px;background:#e9efec;display:grid;place-items:center;font-size:12px;font-weight:900}.workspace-menu div{flex:1}.workspace-menu strong,.workspace-menu small{display:block}.workspace-menu strong{font-size:12px}.workspace-menu small{font-size:12px;color:var(--muted);margin-top:2px}.nav-scroll{flex:1;overflow:auto;padding:0 10px 12px;scrollbar-width:thin;scrollbar-color:#33413d transparent}.nav-group{margin-top:8px}.group-title{width:100%;border:0;background:none;color:#83938e;display:flex;align-items:center;justify-content:space-between;padding:8px 9px;cursor:pointer;text-align:left}.group-title span small,.group-title span strong{display:block}.group-title small{font-size:12px;text-transform:uppercase;letter-spacing:1.2px;color:#667570}.group-title strong{font-size:12px;color:#a9b5b1;margin-top:2px}.group-pages{display:grid;gap:3px}.nav-item{width:100%;border:0;background:none;color:#abb7b3;border-radius:11px;display:flex;align-items:center;gap:10px;padding:9px;cursor:pointer;text-align:left;position:relative}.nav-item:hover{background:rgba(255,255,255,.05);color:#fff}.nav-item.active{background:linear-gradient(90deg,rgba(255,122,0,.18),rgba(255,122,0,.06));color:#fff;box-shadow:inset 3px 0 0 var(--orange)}.nav-icon{width:30px;height:30px;border-radius:9px;display:grid;place-items:center;background:rgba(255,255,255,.04);flex:0 0 auto}.nav-item.active .nav-icon{background:rgba(255,122,0,.18);color:#ffad5e}.nav-copy{flex:1;min-width:0}.nav-copy strong,.nav-copy small{display:block}.nav-copy strong{font-size:12px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}.nav-copy small{font-size:12px;color:#6f7d79;margin-top:2px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}.nav-item:hover .nav-copy small,.nav-item.active .nav-copy small{color:#93a29d}.ev-badge{display:inline-flex;align-items:center;justify-content:center;border-radius:999px;padding:3px 6px;font-size:12px;font-weight:900;white-space:nowrap;background:#e8ecea;color:#4d5a57}.ev-badge.success{background:var(--greenSoft);color:var(--green)}.ev-badge.warning{background:#fff2df;color:#b76510}.ev-badge.danger{background:var(--redSoft);color:var(--red)}.ev-badge.violet{background:var(--violetSoft);color:var(--violet)}.ev-badge.blue{background:var(--blueSoft);color:var(--blue)}.sidebar-footer{padding:9px 10px 13px;border-top:1px solid rgba(255,255,255,.07)}.system-mini{margin:8px 5px 0;padding:9px;display:flex;align-items:center;gap:8px;border-radius:10px;background:rgba(255,255,255,.035)}.health-dot{width:8px;height:8px;border-radius:50%;background:#49d89d;box-shadow:0 0 0 4px rgba(73,216,157,.12)}.system-mini strong,.system-mini small{display:block}.system-mini strong{font-size:12px}.system-mini small{font-size:12px;color:#6f7d79;margin-top:2px}.ev-main{margin-left:var(--sidebar);min-height:100vh;transition:.25s cubic-bezier(.2,.8,.2,1)}.ev-topbar{height:var(--topbar);position:sticky;top:0;z-index:40;background:rgba(255,255,255,.92);backdrop-filter:blur(18px);border-bottom:1px solid var(--line);display:flex;align-items:center;padding:0 20px;gap:16px}.topbar-left{display:flex;align-items:center;gap:10px;min-width:175px}.page-identity small,.page-identity strong{display:block}.page-identity small{font-size:12px;color:#8b9894;text-transform:uppercase;letter-spacing:.9px}.page-identity strong{font-size:12px;margin-top:2px}.universal-search{height:42px;max-width:640px;flex:1;margin:0 auto;border:1px solid #dfe5e2;background:#f5f7f6;border-radius:13px;color:#80908b;display:flex;align-items:center;gap:10px;padding:0 12px;cursor:pointer;text-align:left}.universal-search span{font-size:12px;flex:1;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}.universal-search kbd,.command-palette kbd{font-family:inherit;font-size:12px;border:1px solid #d6ddda;background:#fff;color:#70807b;border-radius:6px;padding:3px 6px;box-shadow:0 1px 0 #cdd5d2}.topbar-actions{display:flex;align-items:center;gap:7px}.context-trigger,.profile-trigger{height:44px;border:1px solid var(--line);background:#fff;border-radius:13px;padding:0 10px;display:flex;align-items:center;gap:8px;cursor:pointer;color:var(--ink)}.context-orb{width:29px;height:29px;border-radius:9px;background:linear-gradient(145deg,#222f2b,#0f1815);color:#ff9a36;display:grid;place-items:center;font-size:12px;font-weight:950}.context-trigger span:nth-child(2),.profile-trigger span:nth-child(2){min-width:0;text-align:left}.context-trigger small,.context-trigger strong,.profile-trigger small,.profile-trigger strong{display:block}.context-trigger small,.profile-trigger small{font-size:12px;color:#8a9793}.context-trigger strong,.profile-trigger strong{font-size:12px;max-width:135px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;margin-top:2px}.quick-button{height:42px;border:0;border-radius:12px;padding:0 14px;background:linear-gradient(135deg,var(--orange),var(--orange2));color:#fff;font-size:12px;font-weight:850;display:flex;align-items:center;gap:7px;cursor:pointer;box-shadow:0 10px 24px rgba(255,122,0,.22)}.alert-button{border-color:var(--line);color:#4a5955;position:relative}.alert-button b{position:absolute;right:-3px;top:-3px;min-width:17px;height:17px;padding:0 4px;border-radius:9px;background:var(--red);color:#fff;font-size:12px;display:grid;place-items:center;border:2px solid #fff}.avatar{width:29px;height:29px;border-radius:9px;background:linear-gradient(145deg,#2b4039,#15201d);color:#fff;display:grid;place-items:center;font-size:12px;font-weight:900}.avatar.large{width:48px;height:48px;border-radius:14px;font-size:13px}.top-popover{position:absolute;top:64px;right:18px;width:380px;background:#fff;border:1px solid var(--line);border-radius:17px;box-shadow:0 25px 80px rgba(22,35,31,.2);z-index:70;overflow:hidden}.top-popover>header{padding:14px 15px;border-bottom:1px solid var(--line);display:flex;align-items:center;justify-content:space-between}.top-popover header small{font-size:12px;color:#8a9793;letter-spacing:1px}.top-popover header h3{font-size:14px;margin:3px 0 0;letter-spacing:-.3px}.top-popover header button{border:0;background:none;color:var(--muted);font-size:12px;cursor:pointer}.context-menu{width:580px}.lineage-flow{display:flex;align-items:center;gap:7px;padding:10px 15px;background:#15201d;color:#fff}.lineage-flow span{font-size:12px;padding:5px 7px;border:1px solid rgba(255,255,255,.12);border-radius:7px}.context-form{padding:12px 15px;display:grid;grid-template-columns:1fr 1fr;gap:8px}.context-form label{display:grid;gap:5px}.context-form label span{font-size:12px;font-weight:800;color:#586660}.context-form select{border:1px solid var(--line);border-radius:9px;background:#fff;padding:8px;font-size:12px;color:var(--ink)}.context-menu footer{padding:10px 15px;border-top:1px solid var(--line);display:flex;justify-content:flex-end;gap:7px}.button{border:0;border-radius:10px;padding:9px 13px;font-size:12px;font-weight:850;display:inline-flex;align-items:center;gap:7px;cursor:pointer}.button.primary{background:linear-gradient(135deg,var(--orange),var(--orange2));color:#fff;box-shadow:0 10px 22px rgba(255,122,0,.18)}.button.ghost{background:#fff;border:1px solid var(--line);color:#46544f}.quick-menu{width:350px;padding-bottom:7px}.quick-menu>button{width:calc(100% - 14px);margin:3px 7px;border:0;background:#fff;border-radius:10px;padding:9px;display:flex;align-items:center;gap:9px;text-align:left;cursor:pointer}.quick-menu>button:hover{background:var(--soft)}.quick-menu>button>span{width:31px;height:31px;border-radius:9px;background:#f2f5f3;color:var(--green);display:grid;place-items:center}.quick-menu>button div{flex:1}.quick-menu strong,.quick-menu small{display:block}.quick-menu strong{font-size:12px}.quick-menu small{font-size:12px;color:var(--muted);margin-top:2px}.notification-menu{width:420px}.notification-list{max-height:420px;overflow:auto}.notification-list button{width:100%;border:0;border-bottom:1px solid var(--line);background:#fff;display:grid;grid-template-columns:auto 1fr auto;gap:9px;padding:11px 14px;text-align:left;cursor:pointer}.notification-list button.unread{background:#fffaf5}.notice-icon{width:33px;height:33px;border-radius:10px;display:grid;place-items:center}.notice-icon.orange{background:#fff1df;color:#c16a13}.notice-icon.blue{background:var(--blueSoft);color:var(--blue)}.notice-icon.red{background:var(--redSoft);color:var(--red)}.notice-icon.green{background:var(--greenSoft);color:var(--green)}.notice-icon.violet{background:var(--violetSoft);color:var(--violet)}.notification-list strong,.notification-list small{display:block}.notification-list strong{font-size:12px}.notification-list p{font-size:12px;color:#6e7b78;line-height:1.4;margin:3px 0}.notification-list small{font-size:12px;color:#9aa4a1}.notification-list button>i{width:6px;height:6px;border-radius:50%;background:var(--orange);margin-top:4px}.notification-menu footer{padding:10px}.notification-menu footer button{width:100%;border:0;background:#17221f;color:#fff;border-radius:10px;padding:9px;display:flex;justify-content:center;align-items:center;gap:7px;font-size:12px;font-weight:800;cursor:pointer}.profile-menu{width:320px;padding:10px}.profile-hero{display:flex;align-items:center;gap:10px;padding:6px}.profile-hero strong,.profile-hero small{display:block}.profile-hero strong{font-size:12px}.profile-hero small{font-size:12px;color:var(--muted);margin-top:2px}.profile-hero p{font-size:12px;color:var(--green);margin:4px 0 0}.profile-hero p i{display:inline-block;width:6px;height:6px;border-radius:50%;background:#42c68f}.profile-scope{border:1px solid var(--line);border-radius:10px;padding:8px;margin:7px 0;display:grid;gap:5px}.profile-scope span{font-size:12px;color:#5d6b66;display:flex;align-items:center;gap:6px}.profile-menu>button{width:100%;border:0;background:#fff;border-radius:9px;padding:8px;display:flex;gap:8px;text-align:left;cursor:pointer;color:var(--ink)}.profile-menu>button:hover{background:var(--soft)}.profile-menu>button>span{flex:1}.profile-menu>button strong,.profile-menu>button small{display:block}.profile-menu>button strong{font-size:12px}.profile-menu>button small{font-size:12px;color:var(--muted);margin-top:2px}.profile-menu>button.danger{color:var(--red)}.context-strip{height:41px;background:#17211e;color:#fff;display:flex;align-items:center;justify-content:space-between;padding:0 22px;gap:12px;overflow:hidden}.context-lineage,.context-meta{display:flex;align-items:center;gap:7px;min-width:0}.context-lineage span,.context-meta span{font-size:12px;color:#c6d0cc;display:flex;align-items:center;gap:5px;white-space:nowrap}.context-lineage b{width:20px;height:20px;border-radius:6px;background:rgba(255,122,0,.15);color:#ffa34b;display:grid;place-items:center;font-size:12px}.context-meta span{color:#93a19c}.context-meta svg{color:#ff9c3e}.context-lineage{flex:1}.context-flow{display:flex;align-items:center;justify-content:center;gap:6px;min-width:0}.context-flow button{height:28px;border:1px solid rgba(255,255,255,.12);background:rgba(255,255,255,.04);color:#eaf3ef;border-radius:8px;padding:0 8px;display:flex;align-items:center;gap:5px;cursor:pointer;min-width:0}.context-flow button:hover{background:rgba(255,255,255,.08)}.context-flow button.primary{border-color:rgba(255,122,0,.42);background:rgba(255,122,0,.14)}.context-flow button.primary:hover{background:rgba(255,122,0,.2)}.context-flow small{text-transform:uppercase;letter-spacing:.7px;font-size:12px;font-weight:900;color:#91a19c}.context-flow strong{font-size:12px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;max-width:132px}.ev-content{min-height:calc(100vh - 117px);padding:22px}.command-backdrop{position:fixed;inset:0;background:rgba(9,16,14,.58);backdrop-filter:blur(8px);z-index:100;display:grid;place-items:start center;padding-top:8vh}.command-palette{width:min(780px,94vw);background:#fff;border-radius:20px;box-shadow:0 35px 120px rgba(0,0,0,.36);overflow:hidden}.command-palette>header{height:62px;display:flex;align-items:center;gap:10px;padding:0 17px;border-bottom:1px solid var(--line)}.command-palette input{border:0;outline:0;flex:1;font-size:14px;color:var(--ink)}.command-filters{display:flex;align-items:center;gap:5px;padding:9px 12px;background:#f7f9f8;border-bottom:1px solid var(--line);overflow:auto}.command-filters button{border:0;background:transparent;border-radius:8px;padding:6px 8px;font-size:12px;color:#687672;white-space:nowrap}.command-filters button.active{background:#17211e;color:#fff}.command-filters span{margin-left:auto;font-size:12px;color:#8d9995}.command-results{max-height:490px;overflow:auto;padding:7px}.command-results button{width:100%;border:0;background:#fff;border-radius:11px;display:flex;align-items:center;gap:10px;padding:10px;text-align:left;cursor:pointer}.command-results button:hover{background:#f5f7f6}.result-icon{width:34px;height:34px;border-radius:10px;background:#edf2ef;color:var(--green);display:grid;place-items:center}.command-results button div{flex:1}.command-results strong,.command-results small{display:block}.command-results strong{font-size:12px}.command-results small{font-size:12px;color:var(--muted);margin-top:3px}.command-palette>footer{padding:9px 13px;border-top:1px solid var(--line);display:flex;align-items:center;gap:13px;color:#7c8985;font-size:12px}.command-palette>footer b{margin-left:auto;color:#45625a}.shell-preview-page{max-width:1600px;margin:0 auto}.preview-hero{background:radial-gradient(circle at 85% 20%,rgba(255,122,0,.18),transparent 30%),linear-gradient(135deg,#17211e,#25342f);color:#fff;border-radius:23px;padding:30px;display:grid;grid-template-columns:minmax(0,1.35fr) 340px;gap:30px;box-shadow:var(--shadow);overflow:hidden}.eyebrow{font-size:12px;font-weight:900;letter-spacing:1.35px;color:#ffad5f}.preview-hero h1{font-size:36px;line-height:1.05;max-width:780px;margin:8px 0 12px;letter-spacing:-1.3px}.preview-hero p{font-size:12px;line-height:1.6;color:#b5c1bd;max-width:760px}.hero-actions{display:flex;gap:8px;margin-top:18px}.preview-hero aside{border:1px solid rgba(255,255,255,.1);background:rgba(255,255,255,.04);border-radius:18px;padding:18px;text-align:center}.preview-hero aside>small,.preview-hero aside>strong{display:block}.preview-hero aside>small{font-size:12px;color:#90a09a;letter-spacing:1px}.preview-hero aside>strong{font-size:13px;margin-top:5px}.preview-hero aside>p{font-size:12px;margin:4px 0;color:#7f908a}.orbit{width:155px;height:155px;margin:0 auto 13px;border:1px dashed rgba(255,255,255,.18);border-radius:50%;position:relative;display:grid;place-items:center}.orbit:before{content:"";position:absolute;inset:23px;border:1px solid rgba(255,255,255,.10);border-radius:50%}.orbit-core{width:52px;height:52px;border-radius:17px;background:linear-gradient(145deg,var(--orange),#ffae51);display:grid;place-items:center;font-weight:950;box-shadow:0 18px 36px rgba(255,122,0,.26)}.orbit i{position:absolute;width:27px;height:27px;border-radius:8px;background:#2c3d37;color:#d9e2de;border:1px solid rgba(255,255,255,.09);display:grid;place-items:center;font-size:12px;font-style:normal;font-weight:900}.orbit .o1{top:4px}.orbit .o2{right:3px}.orbit .o3{bottom:4px}.orbit .o4{left:3px}.preview-metrics{display:grid;grid-template-columns:repeat(4,1fr);gap:12px;margin:14px 0}.preview-metrics article{background:#fff;border:1px solid var(--line);border-radius:16px;padding:14px;display:flex;align-items:center;gap:10px;box-shadow:0 8px 24px rgba(25,37,33,.04)}.metric-icon{width:38px;height:38px;border-radius:12px;display:grid;place-items:center}.metric-icon.violet{background:var(--violetSoft);color:var(--violet)}.metric-icon.blue{background:var(--blueSoft);color:var(--blue)}.metric-icon.orange{background:#fff1df;color:#bf6913}.metric-icon.green{background:var(--greenSoft);color:var(--green)}.preview-metrics small,.preview-metrics strong,.preview-metrics p{display:block}.preview-metrics small{font-size:12px;color:var(--muted)}.preview-metrics strong{font-size:18px;margin-top:2px}.preview-metrics p{font-size:12px;color:#8c9894;margin:2px 0 0}.preview-grid{display:grid;grid-template-columns:minmax(0,1.35fr) 400px;gap:14px}.preview-panel{background:#fff;border:1px solid var(--line);border-radius:18px;padding:16px;box-shadow:0 10px 30px rgba(25,37,33,.05)}.preview-panel>header{display:flex;align-items:center;justify-content:space-between;margin-bottom:12px}.preview-panel h2{font-size:15px;margin:4px 0 0;letter-spacing:-.3px}.route-family-grid{display:grid;grid-template-columns:1fr 1fr;gap:8px}.route-family-grid button{border:1px solid var(--line);background:#fff;border-radius:12px;padding:10px;display:flex;align-items:center;gap:9px;text-align:left;cursor:pointer}.route-family-grid button:hover{border-color:#ffb26c;background:#fffbf6}.route-family-grid button>span{width:32px;height:32px;border-radius:9px;background:#eef3f0;color:var(--green);display:grid;place-items:center}.route-family-grid button div{flex:1}.route-family-grid strong,.route-family-grid small{display:block}.route-family-grid strong{font-size:12px}.route-family-grid small{font-size:12px;color:var(--muted);margin-top:2px}.access-card{grid-row:span 2}.access-card>header>svg{color:var(--green)}.access-ring{width:120px;height:120px;border-radius:50%;margin:18px auto;background:conic-gradient(var(--green) 0 100%,#e9efec 0);display:grid;place-items:center;position:relative}.access-ring:before{content:"";position:absolute;inset:10px;background:#fff;border-radius:50%}.access-ring div{position:relative;text-align:center}.access-ring strong,.access-ring small{display:block}.access-ring strong{font-size:22px}.access-ring small{font-size:12px;color:var(--muted)}.access-card ul{list-style:none;margin:0;padding:0;display:grid;gap:9px}.access-card li{display:flex;gap:8px}.access-card li>span{width:8px;height:8px;border-radius:50%;background:#49c993;margin-top:3px}.access-card li div{flex:1}.access-card li strong,.access-card li small{display:block}.access-card li strong{font-size:12px}.access-card li small{font-size:12px;color:var(--muted);line-height:1.4;margin-top:2px}.workflow-card{grid-column:1}.workflow-line{display:grid;grid-template-columns:1fr auto 1fr auto 1fr auto 1fr;align-items:center;gap:7px}.workflow-line>span{border:1px solid var(--line);border-radius:12px;padding:10px;min-width:0}.workflow-line b{width:24px;height:24px;border-radius:8px;background:#17211e;color:#ff9b3d;display:grid;place-items:center;font-size:12px;margin-bottom:7px}.workflow-line small,.workflow-line strong{display:block}.workflow-line small{font-size:12px;color:var(--muted)}.workflow-line strong{font-size:12px;margin-top:3px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}.workflow-card footer{display:flex;gap:16px;margin-top:10px;padding-top:10px;border-top:1px solid var(--line)}.workflow-card footer span{font-size:12px;color:#61706b;display:flex;align-items:center;gap:5px}.is-collapsed .ev-sidebar{width:92px;padding:0 10px}.is-collapsed .ev-main{margin-left:92px}.is-collapsed .brand-logo,.is-collapsed .workspace-copy,.is-collapsed .workspace-switch>svg,.is-collapsed .group-title span,.is-collapsed .group-title>svg,.is-collapsed .nav-copy,.is-collapsed .nav-item>.ev-badge,.is-collapsed .system-mini div{display:none}.is-collapsed .brand-icon-image{display:block}.is-collapsed .brand-row{padding:0 8px;justify-content:space-between;gap:4px}.is-collapsed .brand{justify-content:center;flex:0 0 40px;width:40px;height:40px;padding:3px}.collapse-button{width:30px;height:30px;border-radius:50%;border:1px solid rgba(255,255,255,.12);background:rgba(255,255,255,.06);color:#fff;display:grid;place-items:center;cursor:pointer;transition:.2s}.collapse-button:hover{background:rgba(255,255,255,.12);transform:scale(1.05)}.collapse-button:active{transform:scale(.95)}.is-collapsed .collapse-button{position:static;width:26px;height:26px;background:rgba(255,255,255,.08);border:1px solid rgba(255,255,255,.14);opacity:1;box-shadow:none;z-index:auto}.is-collapsed .collapse-button svg{color:#fff}.is-collapsed .workspace-switch{justify-content:center;padding:10px 0}
.is-collapsed .workspace-logo{width:36px;height:36px;border-radius:11px}.is-collapsed .group-title{height:8px;padding:2px}.is-collapsed .nav-item{justify-content:center;padding:12px;border-radius:14px}.is-collapsed .nav-icon{width:40px;height:40px}.is-collapsed .sidebar-footer .nav-item{justify-content:center}
.ev-content.route-content{padding:0;overflow-x:auto;overflow-y:visible}.ev-content.route-content>.shadow-page-host{display:block;width:100%;max-width:100%;min-width:0;min-height:calc(100vh - 117px);overflow-x:auto}
.ev-shell[data-theme="light"]{--ink:#17201e;--muted:#6e7b78;--line:#e3e8e6;--soft:#f5f7f6;--shadow:0 20px 50px rgba(28,40,36,.10);background:#f3f5f4;color:#17201e}.ev-shell[data-theme="light"] .ev-sidebar{background:#fff;color:#17201e;border-right:1px solid #e3e8e6;box-shadow:12px 0 40px rgba(28,40,36,.08)}.ev-shell[data-theme="light"] .brand{color:#17201e}.ev-shell[data-theme="light"] .brand-copy small,.ev-shell[data-theme="light"] .workspace-copy small,.ev-shell[data-theme="light"] .group-title small,.ev-shell[data-theme="light"] .nav-copy small,.ev-shell[data-theme="light"] .system-mini small{color:#6e7b78}.ev-shell[data-theme="light"] .workspace-switch,.ev-shell[data-theme="light"] .system-mini{background:#f5f7f6;border-color:#e3e8e6;color:#17201e}.ev-shell[data-theme="light"] .nav-item{color:#3e4b47}.ev-shell[data-theme="light"] .nav-item:hover{background:#f5f7f6;color:#17201e}.ev-shell[data-theme="light"] .nav-item.active{background:linear-gradient(90deg,rgba(255,122,0,.16),rgba(255,122,0,.05));color:#17201e}.ev-shell[data-theme="light"] .nav-icon{background:#edf2ef}.ev-shell[data-theme="light"] .is-collapsed .collapse-button{background:#f5f7f6;border-color:#e3e8e6;color:#17201e}.ev-shell[data-theme="light"] .is-collapsed .collapse-button:hover{background:#e3e8e6}.ev-shell[data-theme="light"] .ev-topbar{background:rgba(255,255,255,.94);border-bottom-color:#e3e8e6}.ev-shell[data-theme="light"] .context-strip{background:#17211e;color:#fff;border-bottom:1px solid #2a3834}.ev-shell[data-theme="light"] .context-lineage span{color:#c6d0cc}.ev-shell[data-theme="light"] .context-meta span{color:#93a19c}.ev-shell[data-theme="light"] .context-lineage b{background:rgba(255,122,0,.15);color:#ffa34b}.ev-shell[data-theme="light"] .top-popover,.ev-shell[data-theme="light"] .command-palette,.ev-shell[data-theme="light"] .sidebar-popover{background:#fff;color:#17201e;border-color:#e3e8e6}.ev-shell[data-theme="light"] .theme-toggle{border-color:#e3e8e6;color:#4a5955}.ev-shell[data-theme="light"] .theme-toggle:hover{background:#f5f7f6}
.ev-shell[data-theme="dark"]{--ink:#f5faf8;--muted:#a7b6b1;--line:#2a3834;--soft:#111a18;--greenSoft:#12362b;--redSoft:#3a1b1d;--blueSoft:#132b48;--violetSoft:#211d3e;--shadow:0 22px 70px rgba(0,0,0,.32);background:#0c1210;color:#f5faf8}.ev-shell[data-theme="dark"] .ev-main,.ev-shell[data-theme="dark"] .ev-content{background:#0c1210}.ev-shell[data-theme="dark"] .ev-sidebar{background:linear-gradient(180deg,#0f1715,#111d19 58%,#0a100e);border-right-color:#2a3834}.ev-shell[data-theme="dark"] .ev-topbar{background:rgba(17,26,24,.94);border-bottom-color:#2a3834;color:#f5faf8}.ev-shell[data-theme="dark"] .universal-search,.ev-shell[data-theme="dark"] .context-trigger,.ev-shell[data-theme="dark"] .profile-trigger,.ev-shell[data-theme="dark"] .icon-button.alert-button,.ev-shell[data-theme="dark"] .top-popover,.ev-shell[data-theme="dark"] .command-palette,.ev-shell[data-theme="dark"] .sidebar-popover{background:#17211e;color:#f5faf8;border-color:#2a3834}.ev-shell[data-theme="dark"] .context-strip{background:#0f1715;border-bottom:1px solid #2a3834}.ev-shell[data-theme="dark"] .workspace-menu button,.ev-shell[data-theme="dark"] .quick-menu>button,.ev-shell[data-theme="dark"] .notification-list button,.ev-shell[data-theme="dark"] .profile-menu>button,.ev-shell[data-theme="dark"] .command-results button{background:#17211e;color:#f5faf8}.ev-shell[data-theme="dark"] .workspace-menu button:hover,.ev-shell[data-theme="dark"] .workspace-menu button.active,.ev-shell[data-theme="dark"] .quick-menu>button:hover,.ev-shell[data-theme="dark"] .profile-menu>button:hover,.ev-shell[data-theme="dark"] .command-results button:hover{background:#111a18}.ev-shell[data-theme="dark"] .command-filters{background:#111a18;border-bottom-color:#2a3834}.ev-shell[data-theme="dark"] .command-palette input{background:#17211e;color:#f5faf8}.ev-shell[data-theme="dark"] .preview-panel,.ev-shell[data-theme="dark"] .preview-metrics article,.ev-shell[data-theme="dark"] .route-family-grid button{background:#17211e;color:#f5faf8;border-color:#2a3834}.ev-shell[data-theme="dark"] .button.ghost{background:#17211e;color:#f5faf8;border-color:#2a3834}.ev-shell[data-theme="dark"] .route-family-grid button:hover{background:#241b12}.ev-shell[data-theme="dark"] .theme-toggle{border-color:#2a3834;color:#f5faf8}
.ev-shell{font-size:var(--ev-font-size-base)!important;line-height:var(--ev-line-height-base)!important}.ev-shell :is(button,input,select,textarea){font-size:var(--ev-font-size-control)!important;line-height:1.35!important}.ev-shell :is(.brand-copy strong){font-size:22px!important}.ev-shell :is(.brand-copy small,.workspace-copy small,.group-title small,.nav-copy small,.system-mini small,.page-identity small,.context-trigger small,.profile-trigger small,.top-popover header small,.quick-menu small,.notification-list small,.profile-menu small,.context-flow small,.context-lineage span,.context-meta span,.command-palette>footer,.ev-badge){font-size:var(--ev-font-size-caption)!important;line-height:1.3!important}.ev-shell :is(.workspace-copy strong,.group-title strong,.nav-copy strong,.system-mini strong,.page-identity strong,.context-trigger strong,.profile-trigger strong,.quick-menu strong,.notification-list strong,.profile-menu strong,.command-results strong,.route-family-grid strong,.access-card li strong,.workflow-line strong){font-size:var(--ev-font-size-meta)!important;line-height:1.35!important}.ev-shell :is(.universal-search span,.notification-list p,.command-results small,.route-family-grid small,.access-card li small,.workflow-line small,.workflow-card footer span){font-size:var(--ev-font-size-meta)!important;line-height:1.4!important}.ev-shell :is(.button,.quick-button,.notification-menu footer button,.context-flow button){font-size:var(--ev-font-size-control)!important}.ev-shell :is(.context-lineage b,.context-orb,.avatar,.workspace-logo){font-size:12px!important}.ev-shell :is(.top-popover header h3,.preview-panel h2){font-size:18px!important}.ev-shell :is(.preview-hero p){font-size:16px!important}.ev-shell :is(.preview-metrics strong){font-size:22px!important}
.ev-sidebar,.nav-scroll,.sidebar-footer{overflow-x:hidden}.ev-sidebar *{min-width:0}.nav-item,.workspace-switch,.system-mini{max-width:100%}.nav-copy strong,.nav-copy small{max-width:100%}.ev-content,.shadow-page-host{min-width:0}.context-strip{min-width:0}.context-lineage,.context-flow,.context-meta{min-width:0}.context-meta{flex-shrink:0}.topbar-left{min-width:auto}
.context-flow button{background:#fff7ef;color:#a74900;border-color:rgba(255,122,0,.45);box-shadow:0 8px 18px rgba(255,122,0,.11)}.context-flow button:hover{background:#fff1df}.context-flow button.primary{background:linear-gradient(135deg,var(--orange),var(--orange2));border-color:transparent;color:#fff}.context-flow button.primary:hover{background:linear-gradient(135deg,#f06f00,var(--orange2))}.command-filters button.active{background:#fff3e8;color:#a74900}.notification-menu footer button{background:linear-gradient(135deg,var(--orange),var(--orange2));color:#fff}
.help-menu{top:auto;bottom:74px;left:12px;right:12px;padding:8px}.help-menu header{display:flex;justify-content:space-between;gap:10px;padding:8px 8px 9px;border-bottom:1px solid var(--line)}.help-menu header small{font-size:12px;color:#8a9793;letter-spacing:1px}.help-menu header h3{font-size:14px;margin:3px 0 0}.help-menu header button{border:0;background:transparent;color:var(--muted);display:grid;place-items:center}.help-menu>button{width:100%;border:0;background:#fff;border-radius:10px;padding:9px;display:grid;grid-template-columns:auto minmax(0,1fr);gap:8px;text-align:left;cursor:pointer;color:var(--ink)}.help-menu>button:hover{background:var(--soft)}.help-menu>button>span{width:31px;height:31px;border-radius:9px;background:#edf2ef;color:var(--green);display:grid;place-items:center}.help-menu strong,.help-menu small{display:block}.help-menu strong{font-size:12px}.help-menu small{font-size:12px;color:var(--muted);margin-top:2px}
.ev-shell :is(h1,h2,h3,h4,p,span,strong,small,em,b,label,button,a,li,dt,dd){white-space:normal;overflow:visible;text-overflow:clip;overflow-wrap:anywhere}
.nav-item,.workspace-switch,.system-mini,.notification-list button,.quick-menu>button,.profile-menu>button{height:auto;min-height:max-content;align-items:flex-start}.context-strip{height:auto;min-height:41px;flex-wrap:wrap;padding-block:6px;overflow:visible}.context-lineage,.context-flow,.context-meta,.topbar-actions{flex-wrap:wrap}.ev-topbar{height:auto;min-height:var(--topbar);flex-wrap:wrap;padding-block:10px}.universal-search{min-width:min(100%,260px)}.ev-content.route-content,.ev-content.route-content>.shadow-page-host{overflow-x:hidden}
.ev-shell :is(.nav-copy strong,.workspace-copy strong,.group-title strong,.page-identity strong,.context-trigger strong,.profile-trigger strong,.quick-menu strong,.notification-list strong,.profile-menu strong,.command-results strong){font-size:14px!important;line-height:1.35!important}.ev-shell :is(.nav-copy small,.workspace-copy small,.group-title small,.system-mini small,.page-identity small,.context-trigger small,.profile-trigger small,.quick-menu small,.notification-list small,.profile-menu small,.command-results small,.context-lineage span,.context-meta span){font-size:13px!important;line-height:1.35!important}.ev-shell :is(.quick-button,.button,.context-flow button,.notification-menu footer button){font-size:15px!important}.ev-shell :is(.ev-badge,.alert-button b,.context-lineage b,.context-flow small){font-size:12px!important}
.ev-shell :is(kbd,.universal-search kbd,.command-palette kbd){font-size:12px!important;line-height:1.2!important}.ev-shell :is(.context-flow strong,.context-lineage span,.context-meta span,.nav-copy strong){font-size:14px!important;line-height:1.35!important}.ev-shell :is(.nav-copy small,.brand-copy small,.workspace-copy small,.group-title small,.system-mini small){font-size:13px!important;line-height:1.35!important}
.ev-shell .group-pages{gap:5px}.ev-shell .nav-item{display:grid;grid-template-columns:30px minmax(0,1fr) auto;align-items:center!important;min-height:66px;height:66px;padding:9px 10px;column-gap:10px;overflow:hidden}.ev-shell .nav-item .nav-icon{grid-column:1;width:30px;height:30px;align-self:center}.ev-shell .nav-item .nav-copy{grid-column:2;display:grid;gap:2px;align-content:center;min-width:0;max-width:100%;overflow:hidden}.ev-shell .nav-item .nav-copy strong{display:block!important;white-space:nowrap!important;overflow:hidden!important;text-overflow:ellipsis!important}.ev-shell .nav-item .nav-copy small{display:-webkit-box!important;-webkit-line-clamp:2;-webkit-box-orient:vertical;white-space:normal!important;overflow:hidden!important;text-overflow:ellipsis!important;overflow-wrap:normal;word-break:normal;line-height:1.3!important}.ev-shell .nav-item>.ev-badge{grid-column:3;align-self:start;justify-self:end;margin-top:1px}.ev-shell .sidebar-footer .nav-item{height:56px;min-height:56px}.ev-shell.is-collapsed .nav-item{display:flex;height:auto;min-height:0;padding:12px;align-items:center;justify-content:center;border-radius:16px;transition:background .18s ease,transform .18s ease}.ev-shell.is-collapsed .nav-item:hover{transform:translateY(-1px)}.ev-shell.is-collapsed .nav-item.active{background:linear-gradient(135deg,rgba(255,122,0,.22),rgba(255,122,0,.08))}
.ev-shell[data-theme="dark"] :is(.universal-search,.context-trigger,.profile-trigger,.top-popover,.command-palette,.sidebar-popover,.quick-menu,.notification-menu,.profile-menu,.help-menu,.preview-panel,.preview-metrics article,.route-family-grid button,.workflow-line>span){background:#17211e!important;color:#f5faf8!important;border-color:#2a3834!important}
.ev-shell[data-theme="dark"] .brand{background:rgba(255,255,255,.03)!important;border:1px solid #2a3834!important;box-shadow:none!important}
.ev-shell[data-theme="dark"] :is(.workspace-menu button,.quick-menu>button,.notification-list button,.profile-menu>button,.command-results button,.help-menu>button,.button.ghost,.context-flow button,.command-filters button.active){background:#111a18!important;color:#f5faf8!important;border-color:#2a3834!important}
.ev-shell[data-theme="dark"] :is(.workspace-menu button.active,.nav-item.active,.context-flow button.primary,.route-family-grid button:hover,.notification-list button.unread){background:#2b2117!important;color:#fff7ed!important;border-color:#ff9f3d!important}
.ev-shell[data-theme="dark"] :is(.universal-search kbd,.command-palette kbd,.ev-badge,.result-icon,.quick-menu>button>span,.help-menu>button>span,.workspace-menu button>span,.route-family-grid button>span,.workflow-line b){background:#24302c!important;color:#dbe8e4!important;border-color:#334640!important}
.ev-shell[data-theme="dark"] :is(.ev-badge.warning,.alert-button b){background:#3a2614!important;color:#ffbf80!important}
.ev-shell[data-theme="dark"] :is(.ev-badge.success){background:#12362b!important;color:#6ee7b7!important}
.ev-shell[data-theme="dark"] :is(.ev-badge.danger){background:#3a1b1d!important;color:#ff9a9a!important}
.ev-shell[data-theme="dark"] :is(.ev-badge.violet){background:#211d3e!important;color:#c4b5fd!important}
.ev-shell[data-theme="dark"] :is(.ev-badge.blue){background:#132b48!important;color:#93c5fd!important}
.ev-shell[data-theme="dark"] .access-ring:before{background:#111a18!important}
.ev-shell[data-theme="dark"] .access-ring strong{color:#f5faf8!important}
.ev-shell[data-theme="dark"] .access-ring small{color:#a7b6b1!important}
@media(max-width:1380px){.context-trigger span:nth-child(2),.profile-trigger span:nth-child(2){display:none}.context-trigger,.profile-trigger{padding:0 7px}.universal-search{max-width:none}.preview-grid{grid-template-columns:1fr 330px}}
@media(max-width:1120px){.ev-sidebar{transform:translateX(-100%)}.ev-main{margin-left:0}.is-collapsed .ev-main{margin-left:0}.mobile-open .ev-sidebar{transform:translateX(0);width:286px}.mobile-menu,.mobile-close{display:grid}.collapse-button{display:none}.mobile-scrim{position:fixed;inset:0;background:rgba(7,13,11,.52);border:0;z-index:45}.context-trigger{display:none}.preview-hero{grid-template-columns:1fr}.preview-hero aside{display:none}.preview-metrics{grid-template-columns:1fr 1fr}.preview-grid{grid-template-columns:1fr}.access-card{grid-row:auto}.workflow-card{grid-column:auto}}
@media(max-width:760px){.ev-topbar{padding:0 10px;gap:8px}.topbar-left{min-width:auto}.page-identity{display:none}.quick-button span{display:none}.quick-button{width:40px;padding:0;justify-content:center}.profile-trigger{display:none}.universal-search span{font-size:0}.universal-search span:after{content:"Search the Admin…";font-size:12px}.context-strip{padding:0 10px}.context-lineage span:not(:nth-of-type(2)){display:none}.context-flow{display:none}.context-meta span{display:none}.ev-content{padding:12px}.context-menu,.notification-menu{width:calc(100vw - 20px);right:10px}.context-form{grid-template-columns:1fr}.preview-hero{padding:21px}.preview-hero h1{font-size:27px}.preview-metrics{grid-template-columns:1fr}.route-family-grid{grid-template-columns:1fr}.workflow-line{grid-template-columns:1fr}.workflow-line>svg{transform:rotate(90deg);margin:auto}.hero-actions{display:grid}.hero-actions .button{justify-content:center}}
`;
